import type { AppState } from "@ch/redux-store/root-reducer";
import moment from "moment";
import { useSelector } from "react-redux";
// import useKemFeatureFlags from "@ch/helpers/utils/postHogAnalytics/useKemFeatureFlags";
import useKemConsentDetails from "./useKemConsentDetails";
import useKemHealthCheck from "./useKemHealthCheck";

const KEM_IC_COMPLETION_PENDING_STORAGE_KEY = "kem_ic_completion_pending";

export type KemGateTarget =
  | "chat"
  | "unavailable"
  | "consent"
  | "ic"
  | "waiting-approval";

export interface KemGateResult {
  target: KemGateTarget;
  hasCheckedHealth: boolean;
}

const useKemGate = (): KemGateResult => {
  const currentUser = useSelector((s: AppState) => s.user.currentUser);
  const kemUserSession = useSelector((s: AppState) => s.kem.kemUserSession);

  const { kemHealthIsHealthy, hasCheckedHealth } = useKemHealthCheck({
    silent: true,
  });

  // const { kem2PilotV1Enabled } = useKemFeatureFlags();

  // consent status from user (refactored into a hook for parity with mobile)
  const { isKemConsentFormApproved, isKemConsentFormPending } =
    useKemConsentDetails();

  const kemICState = kemUserSession?.ic_state;
  const isKemICCompleted = kemICState === "completed";

  const hasPendingICCompletionModal =
    typeof window !== "undefined" &&
    sessionStorage.getItem(KEM_IC_COMPLETION_PENDING_STORAGE_KEY) === "true";

  if (isKemICCompleted && hasPendingICCompletionModal) {
    return { target: "ic", hasCheckedHealth };
  }

  // feature flag gate
  // if (!kem2PilotV1Enabled) return { target: "unavailable", hasCheckedHealth };

  if (hasCheckedHealth && !kemHealthIsHealthy) {
    return { target: "unavailable", hasCheckedHealth };
  }

  if (isKemConsentFormPending) {
    return { target: "waiting-approval", hasCheckedHealth };
  }

  // Age/DOB checks
  const dob = currentUser?.dob;
  const hasValidDOB = !!dob;
  const isUnder18 = dob ? moment().diff(moment(dob), "years") < 18 : false;

  if (hasValidDOB && isUnder18 && !isKemConsentFormApproved) {
    return { target: "consent", hasCheckedHealth };
  }

  return { target: "chat", hasCheckedHealth };
};

export default useKemGate;
